import { PenLine, ScrollText, Landmark } from "lucide-react";

const METRICS = [
  { id: "call", value: "30 min", label: "1,200-investor capital call, end to end" },
  { id: "review", value: "100%", label: "Deliverables reviewed by a senior CA/CPA" },
  { id: "close", value: "Same day", label: "Capital calls, NAV and statements" },
  { id: "trail", value: "Every", label: "Calculation timestamped and exportable" },
];

const CREDENTIALS = [
  {
    id: "review",
    icon: PenLine,
    title: "Senior CA/CPA review",
    body: "On every client deliverable. Nothing reaches an LP or an auditor without a named, credentialed approver behind it.",
  },
  {
    id: "audit-trail",
    icon: ScrollText,
    title: "Full audit trail",
    body: "Every calculation, document, and approval — timestamped and exportable. Your auditors walk the trail instead of requesting it.",
  },
  {
    id: "operators",
    icon: Landmark,
    title: "Built by operators",
    body: "A team that has run PE, real estate, and fund back offices for institutional clients — and knows exactly where the close breaks.",
  },
];

const TALENT = ["BLACKSTONE", "KKR", "GOLDMAN SACHS", "CARLYLE", "APOLLO", "BROOKFIELD", "TPG", "BAIN CAPITAL", "ARES", "MORGAN STANLEY"];

const LogoMark = ({ name }) => (
  <div className="axt-logo-mark flex items-center justify-center h-16 px-8 shrink-0" data-testid={`trust-talent-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <span className="font-display text-lg sm:text-xl tracking-[0.22em] text-[color:var(--axt-text)] whitespace-nowrap">{name}</span>
  </div>
);

export const TrustStrip = () => (
  <section id="trust" data-testid="trust-section" className="relative py-24 sm:py-32 lg:py-40">
    <div className="absolute inset-x-0 top-0 axt-hairline" />
    <div className="max-w-6xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-end" data-reveal>
        <div className="lg:col-span-7">
          <p className="axt-rule mb-5">
            <span>004 · Trust</span>
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em] text-[color:var(--axt-text)]">
            Institutional controls under every automation.
          </h2>
        </div>
        <p className="lg:col-span-5 text-base leading-relaxed text-[color:var(--axt-text-dim)]">
          Senior CA/CPA review on client deliverables. Full audit trail on every calculation, document, and approval, timestamped and exportable.
          Built by operators who have run PE, real estate, and fund back offices for institutional clients.
        </p>
      </div>

      {/* metrics */}
      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px axt-metal rounded-sm overflow-hidden" data-reveal data-testid="trust-metrics">
        {METRICS.map((m) => (
          <div key={m.id} className="bg-[color:var(--axt-obsidian-2)] p-6 sm:p-8" data-testid={`trust-metric-${m.id}`}>
            <div className="font-display text-3xl sm:text-4xl text-[color:var(--axt-text)]">{m.value}</div>
            <div className="mt-2 font-mono-axt text-[11px] tracking-[0.14em] uppercase text-[color:var(--axt-text-faint)]">{m.label}</div>
          </div>
        ))}
      </div>

      {/* credentials */}
      <div className="mt-16 grid md:grid-cols-3 gap-4 sm:gap-5" data-reveal data-testid="trust-credentials">
        {CREDENTIALS.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.id} className="axt-glass p-6 sm:p-7" data-testid={`trust-credential-${c.id}`}>
              <div className="axt-metal w-11 h-11 rounded-sm flex items-center justify-center text-[color:var(--axt-accent)]">
                <Icon className="w-5 h-5" strokeWidth={1.6} />
              </div>
              <h3 className="relative mt-6 font-display text-xl sm:text-2xl text-[color:var(--axt-text)]">{c.title}</h3>
              <p className="relative mt-3 text-sm leading-relaxed text-[color:var(--axt-text-dim)]">{c.body}</p>
            </div>
          );
        })}
      </div>

      {/* talent wall */}
      <div className="mt-16" data-reveal>
        <div className="flex items-center justify-between mb-5">
          <p className="axt-eyebrow" data-testid="trust-talent-label">Our talent comes from</p>
          <p className="hidden sm:block font-mono-axt text-[10px] tracking-[0.14em] uppercase text-[color:var(--axt-text-faint)]">Former operators · PE, RE, credit and fund back offices</p>
        </div>
        <div
          data-testid="trust-logos"
          className="relative overflow-hidden rounded-sm border border-white/20"
          style={{
            maskImage: "linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)",
            WebkitMaskImage: "linear-gradient(90deg, transparent, #000 10%, #000 90%, transparent)",
          }}
        >
          <div className="axt-marquee flex w-max py-3">
            {[...TALENT, ...TALENT].map((name, i) => (
              <LogoMark key={`${name}-${i}`} name={name} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);
